import React, { useRef } from 'react';
import { QrCode, X, Check } from 'lucide-react';
import { useInView } from '../../hooks/useScrollProgress';

const staticPainPoints = [
  'One link forever',
  'Reprint to update',
  'No tracking',
  'Same for everyone',
];

const dynamicBenefits = [
  'Change anytime',
  'Update instantly',
  'Full analytics',
  'Personalized routing',
];

export function ProblemSolution() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { threshold: 0.2, once: true });

  return (
    <section
      ref={sectionRef}
      className="bg-[--clinqr-navy] min-h-screen flex flex-col justify-center section-padding"
      data-section="problem-solution"
    >
      <div className="container-page max-w-5xl">
        {/* Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ease-out ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="label text-[--clinqr-lime] mb-4 font-bold">THE PROBLEM</p>
          <h2 className="heading-1 mb-4 text-white">
            The Problem with Traditional QR Codes
          </h2>
          <p className="body-lg max-w-2xl mx-auto text-white/60">
            Static QR codes lock you into a single destination forever. Dynamic QR codes give you control.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {/* Static QR Card */}
          <div
            className={`relative rounded-3xl border border-white/5 bg-white/5 p-8 transition-all duration-700 ease-out delay-100 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="absolute -top-3 left-6 px-3 py-1 bg-white/10 rounded-full">
              <span className="text-xs font-bold text-white/40 uppercase tracking-wider">Static</span>
            </div>

            {/* QR Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 rounded-2xl bg-white/5 flex items-center justify-center">
                <QrCode className="w-12 h-12 text-white/10" strokeWidth={1.5} />
              </div>
            </div>

            {/* Pain Points */}
            <ul className="space-y-4">
              {staticPainPoints.map((point, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 text-white/40"
                >
                  <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <X className="w-4 h-4 text-white/20" strokeWidth={2.5} />
                  </div>
                  <span className="text-base font-medium">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Dynamic QR Card */}
          <div
            className={`relative rounded-3xl border border-[--clinqr-lime]/20 bg-white/5 p-8 transition-all duration-700 ease-out delay-200 shadow-2xl hover:-translate-y-1 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Badge */}
            <div className="absolute -top-3 left-6 px-3 py-1 bg-[--clinqr-lime] rounded-full">
              <span className="text-xs font-bold text-[--clinqr-navy] uppercase tracking-wider">Dynamic</span>
            </div>

            {/* QR Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 rounded-2xl bg-[--clinqr-lime] flex items-center justify-center shadow-lg">
                <QrCode className="w-12 h-12 text-[--clinqr-navy]" strokeWidth={1.5} />
              </div>
            </div>

            {/* Benefits */}
            <ul className="space-y-4">
              {dynamicBenefits.map((benefit, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 text-[--clinqr-lime]"
                >
                  <div className="w-6 h-6 rounded-full bg-[--clinqr-lime]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[--clinqr-lime]" strokeWidth={2.5} />
                  </div>
                  <span className="text-base font-bold">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Closing Statement */}
        <div
          className={`text-center transition-all duration-700 ease-out delay-300 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-base sm:text-lg text-white/60 max-w-3xl mx-auto leading-relaxed">
            <span className="font-bold text-[--clinqr-lime]">Clinqr</span> gives you QR codes that{' '}
            <span className="font-bold text-white">adapt to your audience</span>, update in real-time, and show you{' '}
            <span className="font-bold text-white">exactly who's scanning</span>.
          </p>
        </div>
      </div>
    </section>
  );
}
